import Button from 'components/Button';
import Error from 'components/form/Error';
import { FlexColumn } from 'components/form/generic';
import PasswordInput from 'components/form/Inputs/PasswordInput';
import PasswordRequirements from 'components/form/PasswordRequirements';
import Field from 'components/profileNavigation/Field';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { mutate } from 'swr';
import isPasswordValid from 'utils/isPasswordValid';
import privateAPI from 'utils/privateAxiosInstance';

const Container = styled.form`
  display: flex;
  flex-direction: column;
  div:first-child {
    border-top: none;
  }
`;

const InputContainer = styled.div`
  & > *:first-child {
    margin-bottom: 0.5rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-self: flex-end;
  margin-top: 2.5rem;
  & > * {
    margin-left: 0.5rem;
  }
`;

const initialError = {
  general: false,
  wrongPassword: false,
  passwordMatch: false,
  passwordStrength: false,
};

const initialState = {
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: '',
  error: initialError,
};

const Password: FC = () => {
  const [state, setState] = useState(initialState);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
      error: initialError,
    });
  };

  const navigate = useNavigate();
  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (state.newPassword !== state.confirmNewPassword) {
      return setState({
        ...state,
        error: { ...initialError, passwordMatch: true },
      });
    }

    if (!isPasswordValid(state.newPassword)) {
      return setState({
        ...state,
        error: { ...initialError, passwordStrength: true },
      });
    }
    privateAPI
      .post('/auth/change-password', {
        currentPassword: state.currentPassword,
        newPassword: state.newPassword,
      })
      .then(() => {
        mutate('/auth/profile');
        navigate('/authentication/login');
      })
      .catch((err) => {
        if (
          err.response &&
          err.response.data &&
          err.response.data.fields &&
          err.response.data.fields.includes('currentPassword')
        ) {
          setState({
            ...state,
            error: { ...initialError, wrongPassword: true },
          });
        } else {
          setState({ ...state, error: { ...initialError, general: true } });
        }
      });
  };
  return (
    <Container onSubmit={handleSubmit}>
      <Field label="Mot de passe actuel">
        <InputContainer>
          {state.error.general && <Error>Une erreur est survenue.</Error>}
          {state.error.wrongPassword && (
            <Error>Le mot de passe est incorrect</Error>
          )}
          <PasswordInput
            value={state.currentPassword}
            onChange={handleChange}
            name="currentPassword"
            required
          />
        </InputContainer>
      </Field>
      <Field label="Nouveau mot de passe">
        <InputContainer>
          <FlexColumn gap={1}>
            {state.error.passwordMatch && (
              <Error>Les mots de passe ne correspondent pas.</Error>
            )}
            {state.error.passwordStrength && (
              <Error>Le mot de passe n'est pas assez fort.</Error>
            )}
            <PasswordInput
              value={state.newPassword}
              onChange={handleChange}
              name="newPassword"
              required
            />
            <PasswordRequirements password={state.newPassword} />
          </FlexColumn>
        </InputContainer>
      </Field>
      <Field label="Confirmer le nouveau mot de passe">
        <PasswordInput
          value={state.confirmNewPassword}
          onChange={handleChange}
          name="confirmNewPassword"
          required
        />
      </Field>
      <ButtonContainer>
        <Button
          type="button"
          variant="outlined"
          onClick={() => navigate('/settings/')}
        >
          Annuler
        </Button>
        <Button type="submit" color="primary">
          Changer le mot de passe
        </Button>
      </ButtonContainer>
    </Container>
  );
};

export default Password;
